import { createTheme, alpha } from '@mui/material/styles';

/*
  Budget Clues design system — single source of truth for visual styling.

  Tokens:
  - Brand: pink primary (#F41468) — the Budget Clues logo pink, used sparingly
    for primary actions, active nav, and the page-header gradient.
  - Surfaces: white cards on a soft cool-grey background.
  - Radius: 8px controls/inputs, 16px cards, 12px dialogs, pill (999px) for chips/toggles.
  - Elevation: only the first few shadow levels are used; they are soft and diffuse.

  Prefer palette references ('primary.main', 'grey.100', theme.palette.*) in
  components instead of hex strings so the app restyles from this file alone.
*/

const BRAND = {
  pink: '#F41468',
  pinkDark: '#c2185b',
  pinkLight: '#f48fb1',
};

// Soft, diffuse shadows for the few elevation levels the app actually uses.
const defaultShadows = createTheme().shadows;
const shadows = [...defaultShadows];
shadows[1] = '0 1px 2px rgba(16, 24, 40, 0.05), 0 1px 3px rgba(16, 24, 40, 0.07)';
shadows[2] = '0 2px 10px rgba(0, 0, 0, 0.08)';
shadows[3] = '0 4px 16px rgba(0, 0, 0, 0.10)';
shadows[4] = '0 6px 24px rgba(0, 0, 0, 0.12)';

const theme = createTheme({
  palette: {
    primary: {
      main: BRAND.pink,
      light: BRAND.pinkLight,
      dark: BRAND.pinkDark,
      contrastText: '#fff',
      lightest: alpha(BRAND.pink, 0.08),
    },
    secondary: {
      main: '#388e3c',
      contrastText: '#fff',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      lightest: 'rgba(76, 175, 80, 0.08)',
      contrastText: '#fff',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      lightest: 'rgba(244, 67, 54, 0.08)',
      contrastText: '#fff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#fff',
    },
    background: {
      default: '#f6f7f9',
      paper: '#ffffff',
      sidebar: '#1e1e1e',
    },
    text: {
      primary: '#1f2937',
      secondary: '#6b7280',
    },
    divider: '#e5e7eb',
    grey: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h4: {
      fontWeight: 700,
      fontSize: 'clamp(1.5rem, 2.5vw, 1.875rem)',
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      fontSize: 'clamp(1.15rem, 2.5vw, 1.35rem)',
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      fontSize: 'clamp(1rem, 2.5vw, 1.05rem)',
    },
    // Legacy custom variant kept for backwards compatibility.
    h7: {
      fontWeight: 500,
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',
      },
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '0.85rem',
    },
    button: {
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
    },
    body1: {
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.875rem',
      },
    },
    body2: {
      fontSize: '0.875rem',
      '@media (max-width:600px)': {
        fontSize: '0.8125rem',
      },
    },
    // Legacy custom variant kept for backwards compatibility (small labels).
    body3: {
      fontSize: '0.8125rem',
      fontWeight: 500,
    },
    caption: {
      fontSize: '0.8125rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*:focus-visible': {
          outline: `2px solid ${alpha(BRAND.pink, 0.5)}`,
          outlineOffset: 2,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          width: 240,
          borderRadius: 0,
          boxShadow: 'none',
          paddingLeft: 12,
          paddingRight: 12,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          transition: 'all 0.2s ease-in-out',
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 10px',
        },
        head: {
          backgroundColor: '#f3f4f6',
          color: '#1f2937',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          padding: '12px 8px',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          WebkitOverflowScrolling: 'touch',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.8125rem',
          borderRadius: 6,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          boxShadow: shadows[3],
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 4,
          fontSize: '0.8125rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.9rem',
          lineHeight: 1.4,
        },
        input: {
          fontSize: '0.9rem',
          lineHeight: 1.4,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.9rem',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND.pink,
            borderWidth: 2,
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${alpha(BRAND.pink, 0.15)}`,
          },
        },
        input: {
          fontSize: '0.9rem',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: '#e5e7eb',
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
  },
});

export default theme;
